import EllipsisOutlined from "@ant-design/icons/EllipsisOutlined";
import NewLookKebab from "features/portfolio/NewLook/NewLookKebab.tsx";
import Dropdown from "common/Dropdown/Dropdown.tsx";
import React, { CSSProperties, useContext, useState } from "react";
import { useTheme } from "styled-components";
import { FollowSignalType } from "@shared/features/signals/common/types.ts";
import { SignalFollowActionButton } from "features/signals/SignalFollow/SignalFollowActionButton.tsx";
import { FollowSignalData, LanguageNameSpaces } from "@shared/types";
import { ItemType } from "antd/es/menu/interface";
import CancelSignalModal from "features/signals/CancelSignal/CancelSignalModal.tsx";
import { useSetAutoTrading } from "features/signals/common/hooks.tsx";
import EditOutlined from "@ant-design/icons/EditOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import PlayCircleOutlined from "@ant-design/icons/PlayCircleOutlined";
import RedoOutlined from "@ant-design/icons/RedoOutlined";
import { SignalBotsIcon } from "common/Icons/Icons.tsx";
import { SIGNAL_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";
import { OnEditModeClicked } from "features/signals/common/types.ts";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import { SignalDetailsContext } from "@shared/features/signals/common/SignalDetails.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface SignalActionsProps {
  signalFollowData: FollowSignalData;
  onEditModeClicked: OnEditModeClicked;
}

const SignalActions = ({
  signalFollowData,
  onEditModeClicked,
}: SignalActionsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  const { showEditSignalActions } = useContext(SignalDetailsContext)!;
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const { onSetAutoTradingClick } = useSetAutoTrading(signalFollowData);

  const actionButtonProps = {
    signal: signalFollowData,
    buttonStyle: {
      width: "100%",
      textAlign: "left",
      color: theme.colors.primaryGrey,
      justifyContent: "flex-start",
      height: "auto",
    } as CSSProperties,
    color: theme.colors.primaryGrey,
    showFullText: true,
  };

  let menuItems: ItemType[] = [
    {
      key: "auto-trading",
      label: t("actions.set_auto_trading"),
      onClick: onSetAutoTradingClick,
      icon: <SignalBotsIcon />,
    },
  ];
  if (signalFollowData.is_open) {
    menuItems = [
      ...menuItems,
      {
        key: "follow",
        label: (
          <SignalFollowActionButton
            followType={FollowSignalType.FOLLOW}
            {...actionButtonProps}
          />
        ),
        icon: <PlayCircleOutlined />,
      },
      {
        key: "update",
        label: (
          <SignalFollowActionButton
            followType={FollowSignalType.UPDATE}
            {...actionButtonProps}
          />
        ),
        icon: <RedoOutlined />,
      },
    ];
  } else {
    menuItems = [
      ...menuItems,
      {
        key: "cancel",
        label: (
          <SignalFollowActionButton
            followType={FollowSignalType.CANCEL}
            {...actionButtonProps}
          />
        ),
        icon: <ExclamationCircleOutlined />,
      },
    ];
  }

  if (showEditSignalActions) {
    menuItems = [
      {
        key: "edit-targets",
        label: t("actions.edit_targets"),
        onClick: () => onEditModeClicked(SIGNAL_EXTRA_DETAILS_TABS.TARGETS),
        icon: <EditOutlined />,
      },
      {
        key: "edit-configuration",
        label: t("actions.edit_configuration"),
        onClick: () =>
          onEditModeClicked(SIGNAL_EXTRA_DETAILS_TABS.CONFIGURATION),
        icon: <SettingOutlined />,
      },
      {
        key: "close",
        label: t("actions.close_signal"),
        onClick: () => setCloseModalOpen(true),
        icon: <DeleteOutlined />,
      },
      ...menuItems,
    ];
  }

  return (
    <>
      <Dropdown
        title={t("actions.signal_actions")}
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={["click"]}
      >
        {theme.isNewLook ? (
          <NewLookKebab
            horizontal={true}
            aria-label={t("actions.signal_actions")}
          />
        ) : (
          <EllipsisOutlined
            style={{
              cursor: "pointer",
              fontSize: "30px",
              color: theme.colors.basic,
            }}
          />
        )}
      </Dropdown>
      <CancelSignalModal
        signalId={signalFollowData.signal_id}
        closeModal={() => setCloseModalOpen(false)}
        visible={closeModalOpen}
      />
    </>
  );
};

export default React.memo(SignalActions);
